import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UserApi } from '../../../api';
import { useContainer } from '../../useContainer';
import { NotificationResponseDto } from '../../../commonTypes/NotificationTypes/notification.types';
import { selectNotifications, setNotifications } from '../../../store/slices/notification-center.slice';

const DEFAULT_LIMIT = 3;

const useHttpGetNotificationsByUserOnMount = () => {
    const dispatch = useDispatch();
    const userApi = useContainer(UserApi);
    const [isReady, setReady] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const notifications = useSelector(selectNotifications);

    const fetchNotifications = (page: number = 1, limit: number = DEFAULT_LIMIT, append: boolean = false) => {
        if (append) setIsLoadingMore(true);

        userApi
            .getNotificationsByUser({
                query: { page, limit },
                onSuccess: res => {
                    const notificationData: NotificationResponseDto = Array.isArray(res.body)
                        ? {
                              items: res.body,
                              total: res.body.length,
                              page: 1,
                              limit: res.body.length,
                              hasMore: false,
                          }
                        : res.body && 'items' in res.body
                        ? res.body
                        : {
                              items: [],
                              total: 0,
                              page: 1,
                              limit,
                              hasMore: false,
                          };

                    dispatch(
                        setNotifications({
                            ...notificationData,
                            items: append ? [...notifications.items, ...notificationData.items] : notificationData.items,
                        })
                    );
                },
                onError: _ => {
                    if (!append) {
                        dispatch(setNotifications({ items: [], total: 0, page: 1, limit, hasMore: false }));
                    }
                },
                onFinally: () => {
                    setReady(true);
                    setIsLoadingMore(false);
                },
            })
            .exec();
    };

    useEffect(() => {
        if (notifications.items.length === 0) {
            fetchNotifications();
        } else {
            setReady(true);
        }
        return () => userApi.getNotificationsByUser({}).abort();
    }, []);

    const loadMore = () => {
        if (!notifications.hasMore || isLoadingMore) return;
        const nextPage = (notifications.page ?? 1) + 1;
        fetchNotifications(nextPage, notifications.limit ?? DEFAULT_LIMIT, true);
    };

    return { data: notifications, isReady, isLoadingMore, loadMore };
};

export default useHttpGetNotificationsByUserOnMount;
